"use client";

import { useState } from "react";
import { Plus, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import faqItems from "../faq.json";
export default function HospitalityIntelligenceFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const container = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const toggleItem = (index: number) => {
    setOpenIndex(prev => prev === index ? null : index);
  };
  return <section data-section-id="3029" id="hospitality-intelligence-faq" className="bg-background text-foreground py-20 md:py-28 lg:py-32 px-6 md:px-10 lg:px-12 border-b border-border/40">
      <div className="max-w-7xl mx-auto">
        <div ref={container.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 transition-all duration-700 cubic-bezier(0.16, 1, 0.3, 1) ${container.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {/* Left Column: Sticky Advisory Card (4-5 cols) */}
          <aside className="lg:col-span-5 xl:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-6 bg-card p-6 md:p-8 rounded-xl border border-border/70 shadow-sm">
              <div className="flex items-center gap-2 text-secondary font-mono text-xs uppercase tracking-[0.2em]">
                <Sparkles className="size-3.5" aria-hidden="true" />
                <span>Straight Answers</span>
              </div>

              <div>
                <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.5rem] font-bold text-foreground leading-tight tracking-tight">
                  Frequently Asked Questions
                </h2>
                <p className="mt-3 text-muted-foreground text-sm md:text-base leading-relaxed">
                  Clear answers on OTA commissions, daily pricing updates, channel managers, and what it takes to increase your take-home revenue.
                </p>
              </div>

              {/* AEO Callout Blockquote */}
              <blockquote className="border-l-4 border-secondary bg-secondary/10 p-4 rounded-lg my-4 text-foreground font-medium italic text-xs md:text-sm leading-relaxed">
                "Revenue management is not about slashing rates to fill beds. It is about selling the right room to the right guest at the right price, every single day."
              </blockquote>

              <div className="pt-2 border-t border-border/60">
                <p className="text-xs text-muted-foreground mb-4">
                  Need a tailored diagnostic for your property portfolio?
                </p>
                <Button asChild variant="secondary" className="w-full h-11 px-6 rounded-full font-mono text-xs tracking-wider uppercase font-semibold transition-transform duration-200 hover:scale-[1.01] active:scale-[0.99]">
                  <Link to="/ContactPage#start-your-revenue-journey">
                    <span>Schedule Strategy Call</span>
                    <ArrowRight className="size-4 ml-1.5" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </div>
          </aside>

          {/* Right Column: Accordion (7-8 cols) */}
          <main className="lg:col-span-7 xl:col-span-8">
            <div className="divide-y divide-border/80 border-y border-border/80 bg-card rounded-xl overflow-hidden shadow-xs">
              {faqItems.map((item, index) => {
              const isOpen = openIndex === index;
              const numeral = String(index + 1).padStart(2, "0");
              return <div data-index={index} key={numeral} className={`transition-colors duration-300 ${isOpen ? "bg-muted/30" : "bg-transparent hover:bg-muted/15"}`}>
                    <Button variant="ghost" onClick={() => toggleItem(index)} className="w-full h-auto text-left py-6 px-5 md:px-7 flex items-center justify-between gap-4 rounded-none hover:bg-transparent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 whitespace-normal" aria-expanded={isOpen} aria-controls={`faq-answer-${index}`}>
                      <div className="flex items-start md:items-center gap-4 md:gap-5 pr-2 text-left flex-1">
                        <span className="font-mono text-xs md:text-sm font-semibold text-secondary pt-0.5 md:pt-0 shrink-0">
                          {numeral}
                        </span>
                        <h3 className={`font-sans text-base md:text-lg font-semibold tracking-tight transition-colors duration-200 ${isOpen ? "text-secondary-foreground font-bold" : "text-foreground"}`}>
                          {item.question}
                        </h3>
                      </div>

                      <div className={`size-8 md:size-9 rounded-lg border flex items-center justify-center shrink-0 transition-all duration-300 ${isOpen ? "bg-secondary text-primary-foreground border-secondary rotate-45 shadow-xs" : "bg-background text-secondary border-border hover:border-secondary/50"}`} aria-hidden="true">
                        <Plus className="size-4 md:size-4.5 transition-transform duration-300" />
                      </div>
                    </Button>

                    <div id={`faq-answer-${index}`} className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <div className="px-5 md:px-7 pb-6 pt-1 pl-12 md:pl-16 text-muted-foreground text-sm md:text-base leading-relaxed">
                          <p>{item.answer}</p>
                        </div>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </main>
        </div>
      </div>
    </section>;
}